export type RoleName = string

// Single role permission set describing capabilities of a role.
export interface RolePermission {
  id: number;
  name: RoleName; // e.g. "Kierowca", "Menadżer", etc.
  description: string | null;
  systemRole: boolean;
  isDriver: boolean;

  // CRUD and feature permissions
  canAddDriver: boolean;
  canCreateCompany: boolean;
  canCreateUser: boolean;
  canEditActivities: boolean;
  canEditActivityDay: boolean;
  canEditAlerts: boolean;
  canEditBillingData: boolean;
  canEditCompany: boolean;
  canEditDelegations: boolean;
  canEditGpsData: boolean;
  canEditUser: boolean;
  canExportData: boolean;
  canGenerateAttestation: boolean;
  canGeneratePrintouts: boolean;
  canImportFiles: boolean;
  canReadDatabaseLogin: boolean;
  canRemoveCompany: boolean;
  canRemoveUser: boolean;
  canViewActivities: boolean;
  canViewAlerts: boolean;
  canViewBillingData: boolean;
  canViewConfiguration: boolean;
  canViewDelegations: boolean;

  // Audit metadata
  created: string | null;
  createdById: string | null;
  modified: string | null;
  modifiedById: string | null;
  deleted: string | null;
  deletedById: string | null;
}

export type ConfigApiRolesResponse = RolePermission[]

/**
 * The server's per-company capability projection for the current user (GET
 * /config-api/user/effective-permissions). Flags are already OR-ed across every role, group and
 * service-account grant that applies, so ONE row is the complete answer for that company.
 *
 * `isServiceAccountScope` marks a row the user reaches ONLY via service-account membership (no
 * CompanyRole of their own) — the company is not "theirs".
 */
export interface CompanyEffectivePermission {
  companyConfigurationEntityId: string;
  isServiceAccountScope: boolean;
  isDriver: boolean;

  canAddDriver: boolean;
  canCreateCompany: boolean;
  canCreateUser: boolean;
  canEditActivities: boolean;
  canEditActivityDay: boolean;
  canEditAlerts: boolean;
  canEditBillingData: boolean;
  canEditCompany: boolean;
  canEditDelegations: boolean;
  canEditGpsData: boolean;
  canEditUser: boolean;
  canExportData: boolean;
  canGenerateAttestation: boolean;
  canGeneratePrintouts: boolean;
  canImportFiles: boolean;
  canReadDatabaseLogin: boolean;
  canRemoveCompany: boolean;
  canRemoveUser: boolean;
  canViewActivities: boolean;
  canViewAlerts: boolean;
  canViewBillingData: boolean;
  canViewConfiguration: boolean;
  canViewDelegations: boolean;
}

/**
 * Adapts the server projection to the RolePermission shape the UI already consumes. Callers only
 * ever OR the can* flags (`.some(r => r.canX)`), so a single pre-OR-ed row is equivalent to the
 * old list of matched roles. Identity fields are synthetic: this is an effective-permission set,
 * not a real role — use getRoleNamesInCompany() when you need role names to display.
 */
export const effectivePermissionToRolePermission = (
  p: CompanyEffectivePermission,
): RolePermission => ({
  id: 0,
  name: '',
  description: null,
  systemRole: false,
  isDriver: p.isDriver,
  canAddDriver: p.canAddDriver,
  canCreateCompany: p.canCreateCompany,
  canCreateUser: p.canCreateUser,
  canEditActivities: p.canEditActivities,
  canEditActivityDay: p.canEditActivityDay,
  canEditAlerts: p.canEditAlerts,
  canEditBillingData: p.canEditBillingData,
  canEditCompany: p.canEditCompany,
  canEditDelegations: p.canEditDelegations,
  canEditGpsData: p.canEditGpsData,
  canEditUser: p.canEditUser,
  canExportData: p.canExportData,
  canGenerateAttestation: p.canGenerateAttestation,
  canGeneratePrintouts: p.canGeneratePrintouts,
  canImportFiles: p.canImportFiles,
  canReadDatabaseLogin: p.canReadDatabaseLogin,
  canRemoveCompany: p.canRemoveCompany,
  canRemoveUser: p.canRemoveUser,
  canViewActivities: p.canViewActivities,
  canViewAlerts: p.canViewAlerts,
  canViewBillingData: p.canViewBillingData,
  canViewConfiguration: p.canViewConfiguration,
  canViewDelegations: p.canViewDelegations,
  created: null,
  createdById: null,
  modified: null,
  modifiedById: null,
  deleted: null,
  deletedById: null,
})

/**
 * The roles-in-company list the UI consumes, resolved from the server's effective-permission
 * projection. Consumers only OR the flags (`.some(r => r.canX)`), so the single pre-OR-ed row is
 * equivalent to the old matched-role list. Returns [] (never [undefined]) when the user has no
 * access to the company, so `.some(...)` denies rather than throwing. Shared by both the legacy
 * roleService cache and the UserContext hook so the lookup rule lives in one place.
 */
export const effectiveRolesInCompany = (
  perms: CompanyEffectivePermission[] | null | undefined,
  companyId: string,
): RolePermission[] => {
  const permission = perms?.find((p) => p.companyConfigurationEntityId == companyId)
  return permission ? [effectivePermissionToRolePermission(permission)] : []
}

export interface Role {
  id: number;
  name: RoleName;
}

export interface CompanyRoleAssignment {
  companyId: number | string; // numeric DB id (serialised as number by the API, string in URLs)
  companyName: string;        // company display name (can be empty string)
  isServiceCompany: boolean;
  roles: Role[];              // one or more roles for this company
}

export interface ServerAccessModel {
  id: number;
  name: string;
  address: string;
  isPrivate: boolean;
}

export interface AccountData {
  cardNumber: string | null;
  companyAdmin: boolean;
  companyId: string;                 // UUID
  companyName: string;               // may be ""
  applyToAllCompanies: boolean;
  createdBy: string | null;          // user id / email / null
  createdDate: string;               // ISO-like timestamp
  demoCompanyAdmin: boolean;
  firstName: string;
  surname?: string;
  globalAdmin: boolean;
  groups: unknown[];                 // no details provided, keep as unknown[]
  id: number;                        // numeric internal id
  isActivated: boolean;
  isDriver: boolean;
  locked: boolean;
  login: string;                     // email/username
  modifiedBy: string | null;
  modifiedDate: string;              // ISO-like timestamp
  paymentAdmin: boolean;
  roles: CompanyRoleAssignment[];    // roles grouped per company
  serverAccess?: ServerAccessModel[];
  // True when the user belongs to a service account (member or owner). Set server-side by
  // GetCurrentUserInfoQueryHandler; used to show the "Konto usługowe" sidebar entry.
  hasServiceAccountAccess?: boolean;
}

export type ConfigApiAccountResponse = AccountData
